import React, { useState, useEffect, useRef } from "react";
import { FaMicrophone, FaPlus, FaFacebookF, FaTwitter, FaInstagram } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const blue = "#08306b";
const yellow = "#fffd33";

async function submitCocktail(newCocktail, selectedBar, audioFiles = {}) {
  const formData = new FormData();

  formData.append("bar_name", selectedBar);
  formData.append("name", newCocktail.name.en);
  formData.append("description", newCocktail.flavor.en);
  formData.append("ingredients", JSON.stringify(newCocktail.ingredients.en));
  formData.append("name_gr", newCocktail.name.gr);
  formData.append("description_gr", newCocktail.flavor.gr);
  formData.append("ingredients_gr", JSON.stringify(newCocktail.ingredients.gr));

  const cleanPrice = parseFloat(newCocktail.price.replace(/[^\d.]/g, ""));
  formData.append("price", cleanPrice);

  if (audioFiles.en) formData.append("voice_en", audioFiles.en);
  if (audioFiles.gr) formData.append("voice_gr", audioFiles.gr);

 
  const response = await fetch(`${process.env.REACT_APP_API_URL}/cocktails`, {
    method: "POST",
    body: formData,
  });

  const ct = response.headers.get("content-type") || "";
  const payload = ct.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const msg = typeof payload === "string" && payload.startsWith("<")
      ? "Server rejected the upload (likely too large)."
      : (payload?.error || `Upload failed with status ${response.status}`);
    throw new Error(msg);
  }
  return payload;

}

function AddCocktailPage() {
  const [bars, setBars] = useState([]);
  const [selectedBar, setSelectedBar] = useState("");

  const [cocktailNameGr, setCocktailNameGr] = useState("");
  const [flavorGr, setFlavorGr] = useState("");
  const [ingredientsGr, setIngredientsGr] = useState([""]);
  const [audioFileGr, setAudioFileGr] = useState(null);

  const [cocktailNameEn, setCocktailNameEn] = useState("");
  const [flavorEn, setFlavorEn] = useState("");
  const [ingredientsEn, setIngredientsEn] = useState([""]);
  const [audioFileEn, setAudioFileEn] = useState(null);

  const [price, setPrice] = useState("");

  const navigate = useNavigate();
  const handleBack = () => {
    navigate("/admin");
  };

  const audioInputGrRef = useRef();
  const audioInputEnRef = useRef();

  useEffect(() => {
    async function fetchBars() {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bars`);
        const data = await response.json();
        setBars(data);
      } catch (error) {
        console.error("Error fetching bars:", error);
      }
    }

    fetchBars();
  }, []);

  const handleAddIngredient = (lang) => {
    if (lang === "gr") setIngredientsGr([...ingredientsGr, ""]);
    else setIngredientsEn([...ingredientsEn, ""]);
  };

  const handleIngredientChange = (lang, index, value) => {
    if (lang === "gr") {
      const updated = [...ingredientsGr];
      updated[index] = value;
      setIngredientsGr(updated);
    } else {
      const updated = [...ingredientsEn];
      updated[index] = value;
      setIngredientsEn(updated);
    }
  };

  const handleSubmit = async(e) => {
    e.preventDefault();

    const newCocktail = {
      name: {
        gr: cocktailNameGr,
        en: cocktailNameEn,
      },
      flavor: {
        gr: flavorGr,
        en: flavorEn,
      },
      ingredients: {
        gr: ingredientsGr,
        en: ingredientsEn,
      },
      price,
    };

    try {
      await submitCocktail(newCocktail, selectedBar, { gr: audioFileGr, en: audioFileEn });
      // Reset only after success
      setSelectedBar(""); setCocktailNameGr(""); setCocktailNameEn("");
      setFlavorGr(""); setFlavorEn("");
      setIngredientsGr([""]); setIngredientsEn([""]);
      setAudioFileGr(null); setAudioFileEn(null); setPrice("");
      if (audioInputGrRef.current) audioInputGrRef.current.value = "";
      if (audioInputEnRef.current) audioInputEnRef.current.value = "";
      alert("Cocktail added!");
    } catch (err) {
      alert(err.message); // or show a nicer UI error
    }
  };

  return (
    <div style={styles.container}>
      {/* Back Button */}
      <button style={styles.back} onClick={handleBack}>←</button>

      <h1 style={styles.title}>Add a New Cocktail</h1>
      <form style={styles.form} onSubmit={handleSubmit}>
        <label style={styles.label}>Select Cocktail Bar</label>
        <select
          value={selectedBar}
          onChange={(e) => setSelectedBar(e.target.value)}
          style={styles.select}
        >
          <option value="" disabled>Select a bar</option>
          {bars.map((bar) => (
            <option key={bar.id} value={bar.name}>
              {bar.name}
            </option>
          ))}
        </select>

        <div style={styles.row}>
          {/* Greek Section */}
          <div style={styles.column}>
            <h3 style={styles.subTitle}>Greek (GR)</h3>

            <label style={styles.label}>Cocktail Name</label>
            <input
              type="text"
              placeholder="π.χ. Μαργαρίτα"
              style={styles.input}
              value={cocktailNameGr}
              onChange={(e) => setCocktailNameGr(e.target.value)}
            />

            <label style={styles.label}>Description</label>
            <textarea
              placeholder="Περιγραφή γεύσης..."
              style={styles.textarea}
              value={flavorGr}
              onChange={(e) => setFlavorGr(e.target.value)}
            />

            <label style={styles.label}>Ingredients</label>
            {ingredientsGr.map((item, index) => (
              <input
                key={index}
                type="text"
                placeholder={`Συστατικό ${index + 1}`}
                value={item}
                onChange={(e) =>
                  handleIngredientChange("gr", index, e.target.value)
                }
                style={styles.input}
              />
            ))}
            <button
              type="button"
              onClick={() => handleAddIngredient("gr")}
              style={styles.addButton}
            >
              <FaPlus style={{ marginRight: 6 }} /> Add Ingredient
            </button>

            <label style={styles.label}>Voice Message (GR)</label>
            <input
              type="file"
              accept=".mp3"
              ref={audioInputGrRef}
              style={{ display: "none" }}
              id="voice-gr"
              onChange={(e) => setAudioFileGr(e.target.files[0] || null)}
            />
            <label htmlFor="voice-gr" style={styles.uploadButton}>
              <FaMicrophone style={{ marginRight: 8 }} />
              Upload GR Voice
            </label>
            {audioFileGr && (
              <div style={styles.fileName}>Selected: {audioFileGr.name}</div>
            )}
          </div>

          {/* English Section */}
          <div style={styles.column}>
            <h3 style={styles.subTitle}>English (EN)</h3>

            <label style={styles.label}>Cocktail Name</label>
            <input
              type="text"
              placeholder="e.g. Margarita"
              style={styles.input}
              value={cocktailNameEn}
              onChange={(e) => setCocktailNameEn(e.target.value)}
            />

            <label style={styles.label}>Description</label>
            <textarea
              placeholder="Flavor description..."
              style={styles.textarea}
              value={flavorEn}
              onChange={(e) => setFlavorEn(e.target.value)}
            />

            <label style={styles.label}>Ingredients</label>
            {ingredientsEn.map((item, index) => (
              <input
                key={index}
                type="text"
                placeholder={`Ingredient ${index + 1}`}
                value={item}
                onChange={(e) =>
                  handleIngredientChange("en", index, e.target.value)
                }
                style={styles.input}
              />
            ))}
            <button
              type="button"
              onClick={() => handleAddIngredient("en")}
              style={styles.addButton}
            >
              <FaPlus style={{ marginRight: 6 }} /> Add Ingredient
            </button>

            <label style={styles.label}>Voice Message (EN)</label>
            <input
              type="file"
              accept=".mp3"
              ref={audioInputEnRef}
              style={{ display: "none" }}
              id="voice-en"
              onChange={(e) => setAudioFileEn(e.target.files[0] || null)}
            />
            <label htmlFor="voice-en" style={styles.uploadButton}>
              <FaMicrophone style={{ marginRight: 8 }} />
              Upload EN Voice
            </label>
            {audioFileEn && (
              <div style={styles.fileName}>Selected: {audioFileEn.name}</div>
            )}
          </div>
        </div>

        <label style={styles.label}>Price (€)</label>
        <input
          type="text"
          inputMode="decimal"
          pattern="^[0-9]+([.][0-9]{1,2})?$"
          placeholder="12.00"
          style={styles.input}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />

        <button type="submit" style={styles.submit}>Add Cocktail</button>
      </form>

      <footer style={styles.footer}>
        <div style={styles.socials}>
          <a href="https://facebook.com" target="_blank" rel="noreferrer" style={styles.iconLink}><FaFacebookF /></a>
          <a href="https://twitter.com" target="_blank" rel="noreferrer" style={styles.iconLink}><FaTwitter /></a>
          <a href="https://instagram.com" target="_blank" rel="noreferrer" style={styles.iconLink}><FaInstagram /></a>
        </div>
        <p style={styles.footerText}>© 2024 Created & Published by</p>
        <p style={styles.footerText}><strong>METRON ARISTON</strong> · Making Responsibility a Trend™</p>
      </footer>
    </div>
  );
}

const styles = {
  container: {
    background: blue,
    padding: "60px 20px 30px",
    fontFamily: "'Segoe UI', sans-serif",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: yellow,
  },
  title: {
    fontSize: "30px",
    marginBottom: "30px",
    color: yellow,
    textAlign: "center",
  },
  subTitle: {
    color: blue,
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  form: {
    background: "#fff",
    borderRadius: "16px",
    padding: "40px",
    maxWidth: "900px",
    width: "100%",
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    marginBottom: "40px",
  },
  row: {
    display: "flex",
    gap: "20px",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  column: {
    flex: 1,
    minWidth: "280px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  label: {
    color: blue,
    fontWeight: "500",
    fontSize: "15px",
    textAlign: "left",
  },
  input: {
    padding: "12px",
    fontSize: "15px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    outline: "none",
  },
  textarea: {
    padding: "12px",
    fontSize: "15px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    height: "100px",
    resize: "none",
    outline: "none",
  },
  submit: {
    padding: "14px",
    backgroundColor: yellow,
    color: blue,
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "16px",
    cursor: "pointer",
    marginTop: "10px",
  },
  addButton: {
    padding: "10px 14px",
    backgroundColor: "transparent",
    color: blue,
    border: `2px solid ${blue}`,
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "14px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    marginTop: "-8px",
  },
  uploadButton: {
    padding: "10px 14px",
    backgroundColor: yellow,
    color: blue,
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "14px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "8px",
  },
  fileName: {
    marginTop: "6px",
    fontSize: "14px",
    color: blue,
  },
  footer: {
    textAlign: "center",
    color: "#eee",
    marginTop: "auto",
    paddingBottom: "20px",
  },
  socials: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    fontSize: "20px",
    marginBottom: "10px",
  },
  iconLink: {
    color: yellow,
    textDecoration: "none",
  },
  footerText: {
    fontSize: "13px",
    margin: "4px 0",
  },
  select: {
    padding: "12px",
    fontSize: "15px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    outline: "none",
    backgroundColor: "#fff",
    color: "#000",
  },
  back: {
    alignSelf: "flex-start",
    fontSize: "24px",
    background: "none",
    border: "none",
    color: yellow,
    cursor: "pointer",
    marginBottom: "20px",
  },
};

export default AddCocktailPage;
